import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, Share2, ChevronRight, Volume2 } from "lucide-react";
import { api, API } from "@/lib/api";
import { useAudio } from "@/context/AudioContext";
import LiveBadge from "@/components/LiveBadge";
import SoundWaves from "@/components/SoundWaves";
import AdBanner from "@/components/AdBanner";
import LiveAudienceCounter from "@/components/LiveAudienceCounter";
import WeatherCarousel from "@/components/WeatherCarousel";
import { RADIO } from "@/constants/testIds";
import { toast } from "sonner";

const RadioLive = () => {
  const [live, setLive] = useState(null);
  const { current, isPlaying, isAudible, playTrack, toggle, volume, setVolume } = useAudio();

  useEffect(() => {
    api.get("/live/radio").then((r) => setLive(r.data)).catch(() => {});
  }, []);

  // Route audio through backend HTTPS proxy — handles HTTP Icecast streams
  // and gracefully 302-redirects for HTTPS upstreams.
  const proxiedSrc = `${API}/stream/radio`;
  const isThisLive = current?.src === proxiedSrc;

  const onPlay = () => {
    if (!live) return;
    if (isThisLive) {
      toggle();
    } else {
      playTrack({
        title: live.program_name,
        subtitle: `com ${live.host} • AO VIVO`,
        cover: live.cover_url,
        src: proxiedSrc,
        isLive: true,
        program: live.program_name,
        host: live.host,
      });
    }
  };

  const onShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Fala Itapira - Rádio Ao Vivo", url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copiado!");
      }
    } catch {}
  };

  return (
    <section className="max-w-6xl mx-auto px-4 md:px-6 pt-6 md:pt-10">
      <div className="mb-5 flex items-baseline justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[var(--fi-blue)]/60 font-bold mb-1">
            Rádio · Streaming
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase text-[var(--fi-blue)] leading-[0.9]">
            No ar agora
          </h1>
        </div>
      </div>

      {/* Live card */}
      <div
        className="relative rounded-2xl overflow-hidden shadow-[0_10px_40px_rgba(13,27,75,0.18)]"
        style={{
          background:
            "linear-gradient(135deg, #0D1B4B 0%, #1a2960 55%, #0D1B4B 100%)",
        }}
      >
        {/* Decorative gradient blob */}
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-30 blur-3xl fi-gradient" />

        <div className="relative p-5 sm:p-8 flex flex-col md:flex-row md:items-center gap-6">
          {/* Cover */}
          <div className="relative flex-shrink-0">
            <img
              src={live?.cover_url || "https://images.unsplash.com/photo-1495020689067-958852a7765e?w=800&q=80"}
              alt="Programa atual"
              className="w-full md:w-56 h-56 object-cover rounded-xl shadow-lg"
            />
            <div className="absolute top-3 left-3 flex flex-col items-start gap-2">
              {live?.is_live === true && <LiveBadge testId={RADIO.liveBadge} />}
              <LiveAudienceCounter channel="radio" />
            </div>
          </div>

          {/* Info + controls */}
          <div className="flex-1 min-w-0 text-white">
            <div className="text-xs uppercase tracking-[0.28em] text-white/60 font-bold mb-2">
              {live?.program_name ? "Programa atual" : "Carregando..."}
            </div>
            <div className="font-display text-3xl sm:text-4xl font-extrabold uppercase leading-tight">
              {live?.program_name || "Fala Itapira"}
            </div>
            <div className="text-white/70 mt-1 text-base">
              com <span className="font-semibold text-white">{live?.host || "—"}</span>
            </div>

            <div className="mt-6 flex items-center gap-4">
              <button
                data-testid={RADIO.playPause}
                onClick={onPlay}
                className="w-16 h-16 rounded-full bg-[var(--fi-red)] text-white flex items-center justify-center shadow-[0_8px_24px_rgba(215,38,56,0.45)] hover:scale-105 active:scale-95 transition-transform"
                aria-label="Tocar rádio ao vivo"
              >
                {isThisLive && isPlaying ? (
                  <Pause className="w-7 h-7" />
                ) : (
                  <Play className="w-7 h-7 ml-1" />
                )}
              </button>

              <SoundWaves playing={isThisLive && isAudible} bars={5} className="h-10" />

              <button
                onClick={onShare}
                className="ml-auto inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm font-semibold uppercase tracking-wider"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Compartilhar</span>
              </button>
            </div>

            {/* Volume */}
            <div className="mt-5 flex items-center gap-3 max-w-sm">
              <Volume2 className="w-4 h-4 text-white/60" />
              <input
                data-testid={RADIO.volume}
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                className="w-full accent-[var(--fi-red)]"
                aria-label="Volume"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Library shortcut */}
      <Link
        to="/radio/biblioteca"
        data-testid={RADIO.goLibrary}
        className="mt-6 flex items-center justify-between rounded-xl bg-white p-5 shadow-[0_8px_30px_rgba(13,27,75,0.08)] hover:shadow-[0_12px_36px_rgba(13,27,75,0.12)] transition-shadow group"
      >
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold mb-1">
            Podcasts & Gravações
          </div>
          <div className="font-display text-2xl font-extrabold uppercase text-[var(--fi-blue)]">
            Ver biblioteca de episódios
          </div>
          <div className="text-sm text-gray-500 mt-1">
            Reveja os programas que já foram ao ar.
          </div>
        </div>
        <ChevronRight className="w-8 h-8 text-[var(--fi-blue)] group-hover:translate-x-1 transition-transform" />
      </Link>

      {/* Ad banner slot */}
      <AdBanner placement="radio" className="mt-6" />

      {/* Weather carousel — right below the radio block */}
      <WeatherCarousel />

      {/* Sponsored / info block */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl bg-white p-5 shadow-[0_4px_20px_rgba(13,27,75,0.06)]">
          <div className="font-display uppercase text-sm tracking-wider text-gray-500 font-bold">
            Cobertura
          </div>
          <div className="font-display text-2xl font-extrabold text-[var(--fi-blue)] mt-1">
            Itapira e Região
          </div>
        </div>
        <div className="rounded-xl bg-white p-5 shadow-[0_4px_20px_rgba(13,27,75,0.06)]">
          <div className="font-display uppercase text-sm tracking-wider text-gray-500 font-bold">
            Streaming
          </div>
          <div className="font-display text-2xl font-extrabold text-[var(--fi-blue)] mt-1">
            24 horas no ar
          </div>
        </div>
        <div className="rounded-xl bg-white p-5 shadow-[0_4px_20px_rgba(13,27,75,0.06)]">
          <div className="font-display uppercase text-sm tracking-wider text-gray-500 font-bold">
            Compartilhe
          </div>
          <div className="font-display text-2xl font-extrabold text-[var(--fi-blue)] mt-1">
            Fala Itapira
          </div>
        </div>
      </div>
    </section>
  );
};

export default RadioLive;
